import React, { useState, useEffect, useMemo } from "react";
import { Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import ProductSelectDialog from "./../../components/ProductSelectDialog";
import ProductScaleDialog from "./components/ProductScaleDialog";
import * as services from "./../../services/saleng";
import { formatNumber } from "../../utils/formatNumber";
import ModernSwal from "../../utils/SwalUtils";
import toast, { Toaster } from "react-hot-toast";
import { useProductsBranch } from "../../hooks/useProductsBranch";
import { hasPermission } from "../../auth/RequirePermission3";
import ImageIcon from "@mui/icons-material/Image";
import ImageModal from "../../components/ImageModal";

const EMPTY_IMAGES = [];

const sortImagesNewestFirst = (images) =>
  images
    .map((image, index) => ({ image, index }))
    .sort((a, b) => {
      const aTime = Date.parse(a.image?.at || "") || 0;
      const bTime = Date.parse(b.image?.at || "") || 0;
      return bTime - aTime || b.index - a.index;
    })
    .map(({ image }) => image);

const ProductImagesButton = ({ images, productName, weight, title }) => {
  const [open, setOpen] = useState(false);
  const sourceImages = Array.isArray(images) ? images : EMPTY_IMAGES;
  const availableImages = useMemo(
    () => sortImagesNewestFirst(sourceImages.filter((image) => image?.url)),
    [sourceImages]
  );
  const imageDetail = useMemo(
    () => ({
      title: `รูป${title}`,
      productName,
      weight,
      images: availableImages.map((image, index) => ({
        id: `${image.url}-${index}`,
        imageUrl: image.url,
        createdAt: image.at,
      })),
    }),
    [availableImages, productName, title, weight]
  );

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          disabled={availableImages.length === 0}
          className="p-2 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="ดูรูปที่บันทึก"
        >
          <ImageIcon style={{ fontSize: 24 }} />
        </button>
        {availableImages.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[9px] font-black ring-2 ring-white">
            {availableImages.length}
          </span>
        )}
      </div>

      <ImageModal
        open={open}
        onClose={() => setOpen(false)}
        imageDetail={imageDetail}
        type="transform"
      />
    </>
  );
};

const ProductItem = ({
  product,
  onDelete,
  onEdit,
  panelTitle,
  readOnly,
}) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-3 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group relative">
    <Toaster position="bottom-left" reverseOrder={false} />
    
    <div className="flex items-start justify-between gap-4">
      <div
        className={`flex-1 ${readOnly ? "cursor-default" : "cursor-pointer"}`}
        onClick={() => !readOnly && onEdit?.(product)}
      >
        <div className="flex items-center gap-3 mb-2">
           <span className="bg-slate-100 text-slate-500 text-sm font-bold px-2 py-0.5 rounded-md w-8 h-8 flex items-center justify-center">
             {product.index}
           </span>
           <h3 className="text-xl font-bold text-slate-800">{product.name}</h3>
           {!readOnly && (
             <EditIcon className="text-slate-300 w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ fontSize: 20 }} />
           )}
        </div>

        {readOnly ? (
          <div className="mt-3">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wide">
              น้ำหนักที่บันทึก
            </span>
            <div className="font-black text-emerald-600 text-2xl leading-tight mt-1">
              {formatNumber(product.total)} กก.
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-6 text-base mt-3">
           <div className="flex flex-col">
              <span className="text-xs text-slate-400 font-bold uppercase tracking-wide">รวม</span>
              <span className="text-lg font-bold text-slate-700">{formatNumber(product.total)}</span>
           </div>
           <div className="h-8 w-px bg-gray-100"></div>
           <div className="flex flex-col">
              <span className="text-xs text-slate-400 font-bold uppercase tracking-wide">หัก</span>
              <span className="text-lg font-bold text-red-500">{formatNumber(product.rest)}</span>
           </div>
           <div className="h-8 w-px bg-gray-100"></div>
           <div className="flex flex-col">
              <span className="text-xs text-slate-400 font-bold uppercase tracking-wide">สุทธิ</span>
              <span className="font-black text-emerald-600 text-2xl leading-tight">{formatNumber(product.net)}</span>
           </div>
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2">
         <ProductImagesButton
            images={product.images}
            productName={product.name}
            weight={product.total}
            title={panelTitle}
         />
         {!readOnly && (
           <button
              onClick={() => onDelete?.(product)}
              className="p-2 rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 transition-colors"
           >
              <DeleteIcon style={{ fontSize: 24 }} />
           </button>
         )}
         
         {!readOnly && hasPermission(["inventory_view_changes"]) && (
            <Chip
                label={`${formatNumber(product.price)} บ./กก.`}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ 
                    fontWeight: "bold", 
                    fontSize: 12, 
                    height: 24, 
                    borderColor: '#e2e8f0', 
                    color: '#64748b' 
                }}
            />
         )}
      </div>
    </div>
  </div>
);

const ProductPanel = ({
  title,
  onAdd,
  products = [],
  summary,
  extra,
  onDelete,
  onEdit,
  readOnly = false,
}) => (
  <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 h-full flex flex-col">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-2xl font-black text-slate-800">{title}</h2>
      {!readOnly && (
        <button
          onClick={onAdd}
          disabled={title === "วัตถุดิบต้นทาง" && products.length > 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-base font-bold transition-colors"
        >
          <AddIcon style={{ fontSize: 22 }} />
          เพิ่มสินค้า
        </button>
      )}
    </div>
    
    <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar min-h-[200px] bg-slate-50/50 rounded-2xl p-2 border border-dashed border-gray-200">
      {products.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-slate-400">
           <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3 text-slate-300">
               <AddIcon style={{ fontSize: 28 }} />
           </div>
           <span className="text-base font-medium">ยังไม่มีสินค้า</span>
        </div>
      ) : (
        products.map((p) => (
          <ProductItem
            key={p.index}
            product={p}
            onDelete={onDelete}
            onEdit={onEdit}
            panelTitle={title}
            readOnly={readOnly}
          />
        ))
      )}
    </div>

    {extra && (
      <div className="mt-4 pt-3 border-t border-gray-100">
         <div className="bg-yellow-50 rounded-xl p-3 text-center border border-yellow-100">
            {extra}
         </div>
      </div>
    )}

    {/* Summary Section */}
    <div
      className="mt-4 bg-slate-50 rounded-2xl p-4 grid gap-2"
      style={{ gridTemplateColumns: `repeat(${summary.length}, minmax(0, 1fr))` }}
    >
      {summary.map((item, i) => (
         <div key={i} className="flex flex-col text-center">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">{item.label}</span>
            <span className={`text-lg font-black ${item.label === 'สุทธิ' ? 'text-slate-800' : 'text-slate-600'}`}>
                {typeof item.value === "number" ? formatNumber(item.value) : item.value}
            </span>
         </div>
      ))}
    </div>
  </div>
);

export default function IndexPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewId = searchParams.get("view");
  const isReadOnly = Boolean(viewId);
  const [productsLeft, setProductsLeft] = useState([]);
  const [productsRight, setProductsRight] = useState([]);
  const [data, setData] = useState([]);
  const [showProduct, setShowProduct] = useState([]);
  const [viewTransaction, setViewTransaction] = useState(null);
  const [detailLoading, setDetailLoading] = useState(isReadOnly);
  const [detailError, setDetailError] = useState("");

  const [summary1, setSummary1] = useState([
    { label: "หน่วย", value: "กก." },
    { label: "รวม", value: 0 },
    { label: "หัก", value: 0 },
    { label: "สุทธิ", value: 0 },
  ]);

  const [summary2, setSummary2] = useState([
    { label: "หน่วย", value: "กก." },
    { label: "รวม", value: 0 },
    { label: "หัก", value: 0 },
    { label: "สุทธิ", value: 0 },
  ]);

  const [extra, setExtra] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogSide, setDialogSide] = useState("left");
  const [openMainProdDialog, setOpenMainProdDialog] = useState(false);
  const [openProductScaleDialog, setOpenProductScaleDialog] = useState(false);
  const { products } = useProductsBranch();

  useEffect(() => {
    if (!isReadOnly) return;

    let active = true;
    async function fetchTransformDetail() {
      setDetailLoading(true);
      setDetailError("");
      try {
        const response = await services.getTransformTransactionById(viewId);
        const transaction = response.data?.data;
        if (!transaction) throw new Error("ไม่พบรายการแปรสภาพ");
        if (!active) return;

        const originWeight = Number(transaction.originWeight) || 0;
        const destinationWeight = Number(transaction.destinationWeight) || 0;
        const originProduct = {
          index: 1,
          productId: transaction.originId,
          name: transaction.origin?.title || "ไม่ระบุสินค้า",
          total: originWeight,
          rest: 0,
          net: originWeight,
          price: 0,
          images: Array.isArray(transaction.originImages)
            ? transaction.originImages
            : [],
        };
        const destinationProduct = {
          index: 1,
          productId: transaction.destinationId,
          name: transaction.destination?.title || "ไม่ระบุสินค้า",
          total: destinationWeight,
          rest: 0,
          net: destinationWeight,
          price: 0,
          images: Array.isArray(transaction.destinationImages)
            ? transaction.destinationImages
            : [],
        };

        setViewTransaction(transaction);
        setProductsLeft([originProduct]);
        setProductsRight([destinationProduct]);
        setSummary1([
          { label: "หน่วย", value: "กก." },
          { label: "น้ำหนักที่บันทึก", value: originWeight },
        ]);
        setSummary2([
          { label: "หน่วย", value: "กก." },
          { label: "น้ำหนักที่บันทึก", value: destinationWeight },
        ]);
        setExtra(originWeight - destinationWeight);
      } catch (error) {
        if (active) {
          setDetailError(
            error?.response?.data?.message ||
              error?.message ||
              "โหลดรายละเอียดไม่สำเร็จ"
          );
        }
      } finally {
        if (active) setDetailLoading(false);
      }
    }

    fetchTransformDetail();
    return () => {
      active = false;
    };
  }, [isReadOnly, viewId]);

  useEffect(() => {
    if (isReadOnly) return;

    async function fetchData() {
      try {
        const res = await services.getInventory();
        const rawData = res.data.data;
        // แปลงข้อมูลจาก rawData
        const transformed = rawData.map((item, index) => {
          return {
            id: index + 1,
            productId: item.productId,
            title: item.product?.title || "",
            basePrice: item.product?.price || 0,
            price: item.averageCost || 0,
            visible: item.quantity > 0,
            branchId: item.branchId,
            categoryId: item.product?.categoryId || null,
            subCategoryId: item.product?.subCategoryId || null,
            category: {
              id: item.product?.category?.id,
              name: item.product?.category?.name,
            },
            subCategory: {
              id: item.product?.subCategory?.id,
              name: item.product?.subCategory?.name,
            },
            image: item.product?.image || null,
            stock: item.quantity || 0,
          };
        });
        setData(transformed);
      } catch (err) {
        console.error("โหลดข้อมูลไม่สำเร็จ", err);
      }
    }

    fetchData();
  }, [isReadOnly]);

  const handleAddLeft = () => {
    setDialogSide("left");
    setOpenMainProdDialog(true);
    setShowProduct(data)
  };

  const handleAddRight = () => {
    setDialogSide("right");
    setOpenMainProdDialog(true);
    setShowProduct(products)
  };

  const updateSummary = (list, isLeft) => {
    const sum = [
      { label: "หน่วย", value: "กก." },
      { label: "รวม", value: list.reduce((s, p) => s + p.total, 0) },
      { label: "หัก", value: list.reduce((s, p) => s + p.rest, 0) },
      { label: "สุทธิ", value: list.reduce((s, p) => s + p.net, 0) },
    ];
    if (isLeft) {
      setSummary1(sum);
      setExtra(sum[3].value - summary2[3]?.value || 0);
    } else {
      setSummary2(sum);
      setExtra(summary1[3]?.value - sum[3].value || 0);
    }
  };

  const handleDelete = (side, productToDelete) => {
    if (side === "left") {
      // ลบสินค้าฝั่งซ้าย
      const newLeft = productsLeft
        .filter((p) => p.index !== productToDelete.index)
        .map((p, i) => ({ ...p, index: i + 1 }));
      setProductsLeft(newLeft);
      updateSummary(newLeft, true);

      // 🔹 ถ้ามีสินค้าฝั่งขวา ให้รีคำนวณราคา
      if (productsRight.length > 0) {
        const totalCostLeft = newLeft.reduce(
          (sum, p) => sum + p.net * p.price,
          0
        );
        const totalNetRight = productsRight.reduce((sum, p) => sum + p.net, 0);
        const avgCostPerKg =
          totalCostLeft > 0 && totalNetRight > 0
            ? totalCostLeft / totalNetRight
            : 0;

        const newRight = productsRight.map((p) => ({
          ...p,
          price: avgCostPerKg,
        }));
        setProductsRight(newRight);
        updateSummary(newRight, false);
      }
    } else {
      // ลบสินค้าฝั่งขวา
      const newRight = productsRight
        .filter((p) => p.index !== productToDelete.index)
        .map((p, i) => ({ ...p, index: i + 1 }));

      // 🔹 คำนวณราคาฝั่งขวาใหม่ตามฝั่งซ้าย
      const totalCostLeft = productsLeft.reduce(
        (sum, p) => sum + p.net * p.price,
        0
      );
      const totalNetRight = newRight.reduce((sum, p) => sum + p.net, 0);
      const avgCostPerKg =
        totalCostLeft > 0 && totalNetRight > 0
          ? totalCostLeft / totalNetRight
          : 0;

      const updatedRight = newRight.map((p) => ({ ...p, price: avgCostPerKg }));

      setProductsRight(updatedRight);
      updateSummary(updatedRight, false);
    }
  };

  const handleEdit = (side, product) => {
    setSelectedProduct({ ...product, side });
    setDialogSide(side);
    setOpenProductScaleDialog(true)
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;

    try {
      const data1 = productsLeft[0]; 
      if (!data1) {
        console.error("❌ ไม่พบสินค้าในฝั่งซ้าย");
        return;
      }

      const confirmResult = await ModernSwal.fire({
          title: "ยืนยันการแปลงสภาพ?",
          text: "คุณต้องการทำรายการแปลงสภาพสินค้านี้ใช่หรือไม่?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "ยืนยัน",
          cancelButtonText: "ยกเลิก",
      });

      if (!confirmResult.isConfirmed) return;
      const payloadSell = {
        productId: data1.productId,
        quantity: data1.total,
        saleTransactionId: null,
        unitCost: data1.price,
        note: "แปลงสภาพ",
      };

      await services.updateStockSell(payloadSell);

      for (const data2 of productsRight) {
        // ส่ง transform สำหรับแต่ละคู่ (ฝั่งซ้ายเดียวกัน)
        const payloadTransform = {
          originWeight: data1.total,
          originId: data1.productId,
          originImages: data1.images || [],
          destinationWeight: data2.total,
          destinationId: data2.productId,
          destinationImages: data2.images || [],
        };

        const payloadReceive = {
          productId: data2.productId,
          quantity: data2.total,
          saleTransactionId: null,
          unitCost: data2.price,
          note: "แปลงสภาพ",
        };

        await services.createTransformTransaction(payloadTransform);
        await services.updateStock(payloadReceive);
        toast.success("การแปรสภาพสำเร็จ", { autoClose: 3000 });
        setTimeout(() => {
          window.history.back();
        }, 3000);
      }
    } catch (error) {
      toast.success("การแปรสภาพไม่สำเร็จ");

      console.error("❌ เกิดข้อผิดพลาด:", error);
    }
  };
  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => cat?.name && !seen.has(cat.name) && seen.add(cat.name));
  }, [products]);

  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      <div className="p-4 bg-white border-b border-gray-100 sticky top-0 z-10">
         <AppBreadcrumbs />
      </div>

      {isReadOnly && (
        <div className="px-6 pt-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <LockOutlinedIcon />
              </div>
              <div>
                <h1 className="text-lg font-black text-slate-800">
                  รายละเอียดการแปรสภาพ (อ่านอย่างเดียว)
                </h1>
                {viewTransaction && (
                  <p className="text-sm text-slate-600 mt-1">
                    วันที่ {new Date(viewTransaction.createdAt).toLocaleString("th-TH")} · แปรสภาพโดย {viewTransaction.transformer?.name || "-"} {viewTransaction.transformer?.lastname || ""}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/stock/transform")}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-amber-200 text-slate-700 hover:bg-amber-100 font-bold transition-colors"
            >
              <ArrowBackIcon />
              กลับหน้ารายการ
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 p-6 overflow-hidden">
        {detailLoading ? (
          <div className="h-full flex items-center justify-center text-slate-500 font-bold">
            กำลังโหลดรายละเอียด...
          </div>
        ) : detailError ? (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <p className="text-rose-600 font-bold">{detailError}</p>
            <button
              type="button"
              onClick={() => navigate("/stock/transform")}
              className="px-5 py-2.5 rounded-xl bg-slate-800 text-white font-bold"
            >
              กลับหน้ารายการ
            </button>
          </div>
        ) : (
          <div className="h-full flex flex-col lg:flex-row gap-6">
            {/* Left Panel */}
            <div className="flex-1 h-full min-h-[400px]">
                <ProductPanel
                    title="วัตถุดิบต้นทาง"
                    onAdd={handleAddLeft}
                    products={productsLeft}
                    summary={summary1}
                    onDelete={(p) => handleDelete("left", p)}
                    onEdit={(p) => handleEdit("left", p)}
                    readOnly={isReadOnly}
                />
            </div>

            {/* Middle Arrow - Hidden on mobile, visible on lg */}
            <div className="hidden lg:flex items-center justify-center">
                <div className="bg-white p-3 rounded-full shadow-sm border border-gray-100 text-slate-400">
                    <DoubleArrowIcon fontSize="large" />
                </div>
            </div>

            {/* Right Panel */}
            <div className="flex-1 h-full flex flex-col gap-4 min-h-[400px]">
                <ProductPanel
                    title="วัตถุดิบปลายทาง"
                    onAdd={handleAddRight}
                    products={productsRight}
                    summary={summary2}
                    extra={
                    <span className={`text-xl font-bold ${extra !== 0 ? "text-rose-500" : "text-emerald-600"}`}>
                        {extra >= 0
                        ? `สูญเสีย: ${formatNumber(extra)}`
                        : `ส่วนเกิน ${formatNumber(Math.abs(extra))}`}
                    </span>
                    }
                    onDelete={(p) => handleDelete("right", p)}
                    onEdit={(p) => handleEdit("right", p)}
                    readOnly={isReadOnly}
                />

                {/* Main Actions */}
                {!isReadOnly && (
                  <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100">
                   <div className="flex gap-4">
                        <button
                            onClick={handleSubmit}
                            disabled={!(productsLeft.length > 0 && productsRight.length > 0)}
                            className="flex-1 h-16 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-black text-xl shadow-lg shadow-emerald-100 disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-2"
                        >
                            <CheckIcon style={{ fontSize: 28 }} />
                            ยืนยัน
                        </button>
                        <button
                             className="flex-1 h-16 bg-white border-2 border-slate-100 text-slate-400 hover:text-rose-500 hover:border-rose-100 rounded-2xl font-bold text-xl transition-all flex items-center justify-center gap-2"
                        >
                            <CloseIcon style={{ fontSize: 28 }} />
                            ยกเลิก
                        </button>
                   </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <>
        <ProductSelectDialog
        open={openMainProdDialog}
        onClose={() => setOpenMainProdDialog(false)}
        onConfirm={(prod) => {
          setSelectedProduct(prod);
          setOpenMainProdDialog(false);
          setOpenProductScaleDialog(true);
        }}
        products={showProduct}
        productsStock={data}

        categories={categories}
      />

      <ProductScaleDialog
        open={openProductScaleDialog}
        onClose={() => {
          setOpenProductScaleDialog(false);
          setSelectedProduct(null);
        }}
        onConfirm={(scale) => {
          if (!selectedProduct) return;
          const side = dialogSide;
          const isLeft = side === "left";

          // เลือก list ของฝั่งที่ต้องแก้ไข
          let productList = isLeft ? [...productsLeft] : [...productsRight];

          // 🔹 อัปเดตสินค้าที่เลือก
          const updatedProduct = {
            ...selectedProduct,
            total: scale.weight,
            rest: scale.deduct,
            net: scale.net,
            price: selectedProduct.price, // เริ่มต้น
            images: scale.images || [],
          };

          // 🔹 หา index ของสินค้าที่แก้ไข
          const index = productList.findIndex(
            (p) => p.index === selectedProduct.index
          );

          if (index >= 0) {
            productList[index] = updatedProduct;
          } else {
            productList.push({
              ...updatedProduct,
              index: productList.length + 1,
              name: selectedProduct.title,
            });
          }

          // 🔹 ถ้าเป็นฝั่งขวา ให้คำนวณราคาใหม่จากฝั่งซ้าย
          if (!isLeft) {
            // total ของฝั่งซ้าย
            const totalCostLeft = productsLeft.reduce(
              (sum, p) => sum + p.net * p.price,
              0
            );

            // รวม net ฝั่งขวา (รวม updatedProduct ที่เพิ่งเพิ่ม)
            const totalNetRight = productList.reduce(
              (sum, p) => sum + p.net,
              0
            );

            const avgCostPerKg =
              totalCostLeft > 0 && totalNetRight > 0
                ? totalCostLeft / totalNetRight
                : 0;

            // 🔹 อัปเดตราคาทุกตัวในฝั่งขวา
            productList = productList.map((p) => ({
              ...p,
              price: avgCostPerKg,
            }));
          } else {
            // กรณีแก้ไขฝั่งซ้าย → อัปเดตราคาฝั่งขวา
            const totalCostLeft = productList.reduce(
              (sum, p) => sum + p.net * p.price,
              0
            );
            const totalNetRight = productsRight.reduce(
              (sum, p) => sum + p.net,
              0
            );
            const avgCostPerKg =
              totalCostLeft > 0 && totalNetRight > 0
                ? totalCostLeft / totalNetRight
                : 0;

            const updatedRight = productsRight.map((p) => ({
              ...p,
              price: avgCostPerKg,
            }));

            setProductsRight(updatedRight);
            updateSummary(updatedRight, false);
          }

          // 🔹 รีเซ็ต index
          const reindexed = productList.map((p, i) => ({ ...p, index: i + 1 }));

          // 🔹 อัปเดต state
          if (isLeft) {
            setProductsLeft(reindexed);
            updateSummary(reindexed, true);
          } else {
            setProductsRight(reindexed);
            updateSummary(reindexed, false);
          }

          setOpenProductScaleDialog(false);
          setSelectedProduct(null);
        }}
        init={{ ...selectedProduct, dialogSide }}
      />
        </>
      )}
    </div>
  );
}
